import React from "react";
import { Link } from "react-router-dom";
import Select from "react-select";

import * as url from "helper/UrlHelper";

import JoditEditorField from "components/Common/JoditEditorField/JoditEditorField";
import LocationField from "components/Common/LocationField/LocationField";
import MultiSelectField from "components/Common/MultiSelectField/MultiSelectField";
import FileUploadField from "components/Common/FileUploadField/FileUploadField";
import { getCustomStyles } from "Config/Config";

const GroupForm = ({
  groupData,
  setGroupField,
  handleLocationFromZip,
  handleImageUpload,
  handleDocumentUpload,
  handleSubmit,
  categoryList,
  searchingList,
  interestList,
  companyList,
  followersOptions,
  setCompanyInput,
  setFollowersInput,
  t,
  isSaving,
}) => {
  return (
    <div className="mt-3 p-3 p-lg-4 border border-gray-300 rounded-10 shadow-sm">
      {/* Basic Info Section */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-3">{t("Basic Info")}</h5>

        {/* Title Field */}
        <div className="form-group mb-3 mb-sm-4">
          <label className="d-block fs-sm fw-semibold mb-2">
            {t("Title")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control fs-sm shadow-none  ${
              groupData.validation.title
                ? "is-invalid"
                : "border border-gray-300"
            }`}
            placeholder={t("e.g., Bay Area Hiking Enthusiasts")}
            value={groupData.title}
            onChange={(e) => {
              setGroupField("title")(e.target.value);
              setGroupField("validation")({
                ...groupData.validation,
                title: false,
              });
            }}
          />
          {groupData.validation.title && (
            <div className="invalid-feedback fs-md">
              {t("A group title is required")} *
            </div>
          )}
        </div>

        {/* Summary Field */}
        <div className="form-group mb-3 mb-sm-4">
          <label className="d-block fs-sm fw-semibold mb-2">
            {t("Summary")}
          </label>
          <textarea
            className="form-control fs-sm shadow-none border border-gray-300"
            placeholder={t(
              "Write a brief and engaging description of your group's purpose and activities."
            )}
            rows={5}
            value={groupData.summary}
            onChange={(e) => setGroupField("summary")(e.target.value)}
          />
        </div>

        {/* Details Editor */}
        <JoditEditorField
          label={t("Details")}
          labelClassName="d-block fs-sm fw-semibold mb-2"
          value={groupData.details}
          onChange={(newContent) => setGroupField("details")(newContent)}
        />
      </div>

      {/* Location & Company */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-3">{t("Location & Company")}</h5>
        <div className="row">
          <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
            <LocationField
              label={t("Address")}
              value={groupData.location}
              onChange={(e) => setGroupField("location")(e.target.value)}
              icon="my_location"
              placeholder={t("Enter the group's physical address")}
            />
          </div>
          <div className="col-lg-6">
            <div className="form-group mb-3">
              <label className="d-block fs-sm fw-semibold mb-2">
                {t("Company")}
              </label>
              <Select
                styles={getCustomStyles()}
                placeholder={t("Search and select a company (if applicable)")}
                isClearable
                options={companyList}
                value={groupData.company}
                onChange={setGroupField("company")}
                onInputChange={setCompanyInput}
              />
            </div>
          </div>
        </div>

        {/* Location Fields */}
        <div className="form-group mb-3">
          <div className="row">
            <div className="col-lg-4 mb-3 mb-lg-0">
              <div className="row">
                <div className="col">
                  <input
                    type="text"
                    className="form-control shadow-none border border-gray-300"
                    placeholder={t("Enter a Zip Code")}
                    value={groupData.zipcode}
                    onChange={(e) => setGroupField("zipcode")(e.target.value)}
                  />
                </div>
                <div className="col">
                  <button
                    className="btn btn-primary"
                    onClick={handleLocationFromZip}
                  >
                    {t("Fetch Location")}
                  </button>
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="row">
                <div className="col-lg-4 mb-3 mb-lg-0">
                  <input
                    type="text"
                    className="form-control shadow-none border border-gray-300"
                    placeholder={t("e.g., San Francisco")}
                    value={groupData.city}
                    onChange={(e) => setGroupField("city")(e.target.value)}
                  />
                </div>
                <div className="col-lg-4 mb-3 mb-lg-0">
                  <input
                    type="text"
                    className="form-control shadow-none border border-gray-300"
                    placeholder={t("e.g., California")}
                    value={groupData.state}
                    onChange={(e) => setGroupField("state")(e.target.value)}
                  />
                </div>
                <div className="col-lg-4">
                  <input
                    type="text"
                    className="form-control shadow-none border border-gray-300"
                    placeholder={t("e.g., United States")}
                    value={groupData.country}
                    onChange={(e) => setGroupField("country")(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coordinates */}
        <div className="form-group mb-3 mb-sm-4 ">
          <label className="d-block fs-sm fw-semibold mb-2">
            {t("Geo Location")}
          </label>
          <div className="d-flex flex-column flex-lg-row gap-3 align-items-lg-center">
            <input
              type="text"
              className="form-control border border-gray-300 shadow-none"
              placeholder={t("e.g., 37.7749 (Latitude)")}
              value={groupData.latitude}
              onChange={(e) => setGroupField("latitude")(e.target.value)}
            />
            <input
              type="text"
              className="form-control border border-gray-300 shadow-none"
              placeholder={t("e.g., -122.4194 (Longitude)")}
              value={groupData.longitude}
              onChange={(e) => setGroupField("longitude")(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Objective Editor */}
      <div className="form-group mb-4 p-3 pb-0 border rounded-8 shadow-sm bg-white">
        <JoditEditorField
          label={t("Objective")}
          labelClassName="d-block fs-sm fw-semibold mb-2"
          value={groupData.objective}
          onChange={(newContent) => setGroupField("objective")(newContent)}
        />
      </div>

      {/* Categories & Skills */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-3">
          {t("Categories, Searchings, Interests & Followers")}
        </h5>
        <div className="row">
          <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
            <MultiSelectField
              label={t("Category")}
              options={categoryList}
              value={groupData.category}
              onChange={setGroupField("category")}
              placeholder={t("Choose relevant categories for your group")}
              styles={getCustomStyles()}
            />
          </div>
          <div className="col-lg-6">
            <MultiSelectField
              label={t("Searching")}
              options={searchingList}
              value={groupData.searching}
              onChange={setGroupField("searching")}
              placeholder={t("e.g., Web Developers, Project Managers")}
              styles={getCustomStyles()}
            />
          </div>
        </div>

        {/* Interests & Budget */}
        <div className="row mt-3">
          <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
            <MultiSelectField
              label={t("Interest")}
              options={interestList}
              value={groupData.interest}
              onChange={setGroupField("interest")}
              placeholder={t("Select interests that match your group")}
              styles={getCustomStyles()}
            />
          </div>
          <div className="col-lg-6">
            <div className="form-group">
              <label className="d-block fs-sm fw-semibold mb-2">
                {t("Budget")}
              </label>
              <input
                type="text"
                className="form-control fs-sm shadow-none border border-gray-300"
                placeholder={t("e.g., $500 (optional)")}
                value={groupData.budget}
                onChange={(e) => setGroupField("budget")(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <MultiSelectField
            label={t("Followers")}
            options={followersOptions}
            value={groupData.followers}
            onChange={setGroupField("followers")}
            placeholder={t("Type user name or email")}
            styles={getCustomStyles()}
            onInputChange={setFollowersInput}
          />
        </div>
      </div>

      {/* Image Upload */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-4">{t("Group Logo and Documents")}</h5>
        <div className="mb-4">
          <label className="d-block fs-sm fw-semibold mb-2">
            {t("Group Logo")}
          </label>
          <FileUploadField
            label={t("Logo")}
            accept="JPG, PNG, JPEG"
            onUpload={handleImageUpload}
            files={
              groupData.image === ""
                ? [
                    {
                      path: "https://placehold.co/150x150",
                      name: "Group Image",
                    },
                  ]
                : [
                    {
                      path: url.SERVER_URL + groupData.image,
                      name: "Group Image",
                    },
                  ]
            }
            onRemove={() => {
              setGroupField("image")("");
              setGroupField("imageId")(null);
            }}
            loading={groupData.isUploadingImage}
            fileType="image"
          />
        </div>

        {/* Document Upload */}
        <div className="mb-0">
          <label className="d-block fs-sm fw-semibold mb-2">
            {t("Upload Documents")}
          </label>
          <FileUploadField
            label={t("Upload Documents")}
            accept="Max 20mb doc/img/mp4"
            onUpload={handleDocumentUpload}
            files={groupData.documents}
            onRemove={(index) => {
              const updatedDocs = [...groupData.documents];
              const updatedDocIds = [...groupData.documentIds];

              updatedDocs.splice(index, 1);
              updatedDocIds.splice(index, 1);

              setGroupField("documents")(updatedDocs);
              setGroupField("documentIds")(updatedDocIds);
            }}
            loading={groupData.isUploadingDocs}
            multiple
            fileType="document"
          />
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
        <h5 className="mb-3">{t("Terms and Conditions")}</h5>
        <div className="form-check mb-2">
          <input
            className="form-check-input me-2"
            type="checkbox"
            checked={groupData.isPrivate}
            onChange={(e) => setGroupField("isPrivate")(e.target.checked)}
            id="privategroup"
          />
          <label className="form-check-label" htmlFor="privategroup">
            {t("Mark as private group")}
          </label>
        </div>

        {!groupData.isPrivate && (
          <div className="form-check mb-2">
            <input
              className="form-check-input me-2"
              type="checkbox"
              checked={groupData.autoAccept}
              onChange={(e) => setGroupField("autoAccept")(e.target.checked)}
              id="autoAccept"
            />
            <label className="form-check-label" htmlFor="autoAccept">
              {t("Auto accept joining invitations")}
            </label>
          </div>
        )}
      </div>

      {/* Error Message */}
      {groupData.error && groupData.error !== "" && (
        <div className="alert alert-danger" role="alert">
          {groupData.error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="action d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <Link
          to="/admin/group/list"
          className="btn btn-outline-primary d-flex align-items-center gap-2"
        >
          <span className="material-symbols-outlined">cancel</span>
          {t("Cancel")}
        </Link>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
          disabled={isSaving}
          onClick={handleSubmit}
        >
          {isSaving ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {t("Saving...")}
            </>
          ) : (
            <>
              <span className="material-symbols-outlined">save</span>
              {t("Save Group")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default GroupForm;
